// Southington Dynamic Content API Utilities
// Collection of free public APIs for Southington, CT content

// Coordinates for Southington, CT
export const SOUTHINGTON_COORDS = {
  lat: 41.5959,
  lon: -72.8773,
  zipCode: '06489'
};

// Weather API using Open-Meteo (free, no API key required)
export const fetchWeatherData = async () => {
  try {
    const response = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${SOUTHINGTON_COORDS.lat}&longitude=${SOUTHINGTON_COORDS.lon}&current_weather=true&hourly=temperature_2m,precipitation_probability,weathercode&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=America%2FNew_York&forecast_days=3`
    );
    
    if (!response.ok) throw new Error('Weather API failed');
    return await response.json();
  } catch (error) {
    console.error('Weather fetch error:', error);
    return null;
  }
};

// Air Quality API using Open-Meteo
export const fetchAirQuality = async () => {
  try {
    const response = await fetch(
      `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${SOUTHINGTON_COORDS.lat}&longitude=${SOUTHINGTON_COORDS.lon}&current=us_aqi&timezone=America%2FNew_York`
    );
    
    if (!response.ok) throw new Error('Air Quality API failed');
    return await response.json();
  } catch (error) {
    console.error('Air Quality fetch error:', error);
    return null;
  }
};

// Sunrise/Sunset API
export const fetchSunriseSunset = async () => {
  try {
    const response = await fetch(
      `https://api.sunrise-sunset.org/json?lat=${SOUTHINGTON_COORDS.lat}&lng=${SOUTHINGTON_COORDS.lon}&formatted=0`
    );
    
    if (!response.ok) throw new Error('Sunrise API failed');
    const data = await response.json();
    return data.status === 'OK' ? data.results : null;
  } catch (error) {
    console.error('Sunrise fetch error:', error);
    return null;
  }
};

// Mock local events (in production, could use Eventbrite API, local RSS feeds, etc.)
export const fetchLocalEvents = async () => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return [
    {
      id: 1,
      title: "Southington Apple Harvest Festival",
      date: "2025-10-04",
      time: "10:00 AM - 6:00 PM",
      location: "Town Green",
      description: "Annual celebration featuring local vendors, live music, and family activities.",
      category: "Festival",
      url: "https://www.southington.org"
    },
    {
      id: 2,
      title: "Town Council Meeting",
      date: "2025-01-15",
      time: "7:00 PM",
      location: "Town Hall",
      description: "Monthly public meeting to discuss town business and community issues.",
      category: "Government",
      url: "https://www.southington.org"
    },
    {
      id: 3,
      title: "Library Book Club",
      date: "2025-01-20",
      time: "6:30 PM",
      location: "Southington Library",
      description: "Monthly book discussion group. This month: local Connecticut authors.",
      category: "Education",
      url: "https://www.southingtonlibrary.org"
    },
    {
      id: 4,
      title: "Winter Farmers Market",
      date: "2025-01-25",
      time: "9:00 AM - 1:00 PM",
      location: "Community Center",
      description: "Indoor farmers market featuring local produce and artisan goods.",
      category: "Market",
      url: "https://www.southington.org"
    }
  ];
};

// Local news using RSS feeds (free, no API key required)
export const fetchLocalNews = async () => {
  try {
    // Using RSS2JSON service to convert RSS feeds to JSON
    const rssFeeds = [
      'https://www.southington.org/rss.xml', // Town RSS if available
      'https://www.record-journal.com/rss.xml', // Local newspaper
      'https://www.ctpost.com/rss/feed/Connecticut-News-1.xml' // CT news
    ];
    
    const newsItems = [];
    
    // Try to fetch from multiple RSS sources
    for (const feedUrl of rssFeeds) {
      try {
        const rssToJsonUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(feedUrl)}&count=3`;
        const response = await fetch(rssToJsonUrl);
        
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'ok' && data.items) {
            const processedItems = data.items.map(item => ({
              id: item.guid || item.link,
              title: item.title,
              summary: item.description?.replace(/<[^>]*>/g, '').substring(0, 150) + '...',
              url: item.link,
              publishedAt: item.pubDate,
              source: data.feed?.title || 'Local News',
              category: 'Local News'
            }));
            newsItems.push(...processedItems);
          }
        }
      } catch (feedError) {
        console.warn(`Failed to fetch from ${feedUrl}:`, feedError);
      }
    }
    
    // If RSS feeds fail, fall back to mock data
    if (newsItems.length === 0) {
      return [
        {
          id: 1,
          title: "Southington Town Council Meeting Scheduled",
          summary: "Monthly town council meeting to discuss budget and local initiatives.",
          url: "https://www.southington.org",
          publishedAt: new Date().toISOString(),
          source: "Town of Southington",
          category: "Government"
        },
        {
          id: 2,
          title: "Apple Harvest Festival Planning Underway",
          summary: "Annual Apple Harvest Festival preparations begin for this fall's celebration.",
          url: "https://www.southington.org",
          publishedAt: new Date(Date.now() - 86400000).toISOString(),
          source: "Southington Events",
          category: "Community"
        },
        {
          id: 3,
          title: "Southington Library Announces New Programs",
          summary: "Community programs and events scheduled for the upcoming season.",
          url: "https://www.southingtonlibrary.org",
          publishedAt: new Date(Date.now() - 172800000).toISOString(),
          source: "Southington Library",
          category: "Education"
        }
      ];
    }
    
    // Sort by date and return most recent
    return newsItems
      .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
      .slice(0, 5);
      
  } catch (error) {
    console.error('Local news fetch error:', error);
    // Return mock data as fallback
    return [
      {
        id: 1,
        title: "Southington Community Updates Available",
        summary: "Check the town website for the latest community news and updates.",
        url: "https://www.southington.org",
        publishedAt: new Date().toISOString(),
        source: "Town of Southington",
        category: "Community"
      }
    ];
  }
};

// Historical facts about Southington
export const getSouthingtonFacts = () => {
  return [
    {
      category: "History",
      fact: "Southington was incorporated in 1779, originally part of Farmington."
    },
    {
      category: "Geography",
      fact: "The town covers 36.4 square miles with elevations ranging from 200 to 900 feet."
    },
    {
      category: "Economy",
      fact: "Known for manufacturing, including the historic Peck, Stow & Wilcox Company."
    },
    {
      category: "Culture",
      fact: "Home to the annual Apple Harvest Festival, celebrating local agriculture since 1969."
    },
    {
      category: "Nature",
      fact: "Features over 20 parks and recreational areas, including Crescent Lake."
    }
  ];
};

// Weather condition mapping
export const getWeatherDescription = (weatherCode) => {
  const descriptions = {
    0: "Clear sky",
    1: "Mainly clear",
    2: "Partly cloudy", 
    3: "Overcast",
    45: "Fog",
    48: "Depositing rime fog",
    51: "Light drizzle",
    53: "Moderate drizzle",
    55: "Dense drizzle",
    56: "Light freezing drizzle",
    57: "Dense freezing drizzle",
    61: "Slight rain",
    63: "Moderate rain",
    65: "Heavy rain",
    66: "Light freezing rain",
    67: "Heavy freezing rain",
    71: "Slight snow fall",
    73: "Moderate snow fall",
    75: "Heavy snow fall",
    77: "Snow grains",
    80: "Slight rain showers",
    81: "Moderate rain showers",
    82: "Violent rain showers",
    85: "Slight snow showers",
    86: "Heavy snow showers",
    95: "Thunderstorm",
    96: "Thunderstorm with slight hail",
    99: "Thunderstorm with heavy hail"
  };
  
  return descriptions[weatherCode] || "Unknown conditions";
};

// Air Quality Index interpretation
export const getAQIDescription = (aqi) => {
  if (aqi <= 50) return { level: "Good", color: "#00e400", description: "Air quality is satisfactory" };
  if (aqi <= 100) return { level: "Moderate", color: "#ffff00", description: "Air quality is acceptable" };
  if (aqi <= 150) return { level: "Unhealthy for Sensitive Groups", color: "#ff7e00", description: "Sensitive individuals may experience problems" };
  if (aqi <= 200) return { level: "Unhealthy", color: "#ff0000", description: "Everyone may experience problems" };
  if (aqi <= 300) return { level: "Very Unhealthy", color: "#8f3f97", description: "Health alert for everyone" };
  return { level: "Hazardous", color: "#7e0023", description: "Emergency conditions" };
};

// Format time for local timezone
export const formatLocalTime = (utcTime) => {
  return new Date(utcTime).toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};